import { Suspense, useMemo } from 'react'

import { useIsMobile } from 'src/shared/hooks/use-mobile'
import { cn } from 'src/shared/utils/cn'

import { useUserTransactions } from '../../../api/marketplace/use-user-transactions'
import { useUserPrivacyPreferences } from '../../../api/preferences/use-user-privacy-preferences'
import { usePublicProfileStore } from '../stores/use-public-profile-store'

import NoTransactionDisclaimer from './no-transactions'
import TransactionCard from './transaction-card'
import TransactionVolumeCard from './transaction-volume-card'
import TransactionsContentSkeleton from './transactions-content-skeleton'

const TransactionsContentSuspense = ({ userId }: { userId: number }) => {
  const { data: preferences } = useUserPrivacyPreferences(userId)
  const isMobile = useIsMobile()
  const { isPublicProfile } = usePublicProfileStore()
  const { data } = useUserTransactions(userId, 'received')
  const transactions = data?.transactions
  const totalVolume = data?.totalVolume

  const showTransactions = preferences?.transactionsVisibleToConsumer

  const transactionList = useMemo(() => {
    return transactions?.map((transaction, index) => <TransactionCard key={index} transaction={transaction} />)
  }, [transactions])

  return (
    <div
      className={cn(
        'flex w-full flex-shrink-0 flex-grow-0 flex-col gap-3 text-clip',
        // in private profile, we need to subtract the width of the sidebars and the margin of the container
        // 100vw - 40rem (2 sidebars) - 3rem (inner container padding) - 2rem (left sidebar margin) - 2.5rem (right sidebar margin)
        !isMobile ? (!isPublicProfile ? 'max-w-[calc(100vw-40rem-3rem-2rem-2.5rem)]' : 'max-w-3xl') : 'max-w-full',
      )}
    >
      <TransactionVolumeCard totalVolume={totalVolume} totalTransactions={transactions?.length ?? 0} />
      {showTransactions ? (
        <div className="mt-4 flex flex-col text-clip">{transactionList}</div>
      ) : (
        <NoTransactionDisclaimer userId={userId} />
      )}
    </div>
  )
}

const TransactionsContent = ({ userId }: { userId: number }) => {
  return (
    <Suspense fallback={<TransactionsContentSkeleton />}>
      <TransactionsContentSuspense userId={userId} />
    </Suspense>
  )
}

export default TransactionsContent
